import { useEffect, useState } from 'react';
import moment, { type Moment } from 'moment-timezone';
import { type SchedulerDetailsTypes } from '@/components/shared/interview-scheduler/SchedulerDetails/SchedulerDetails.types';
import { type DatesSetArg } from '@fullcalendar/core';
import { schedulerDetailsUtils } from '@/components/shared/interview-scheduler/SchedulerDetails/utils/schedulerDetails.utils';

interface UseSchFindTimeProps {
  interviewDataRef: { current: SchedulerDetailsTypes.InterviewData };
  registerForUpdates: (key: string, callback: () => void) => () => void;
  updateSharedData: (
    data: Partial<SchedulerDetailsTypes.InterviewData>
  ) => void;
  schedulerState: SchedulerDetailsTypes.InterviewData['schedulerState'];
  calendarRef: React.RefObject<any>; // FullCalendar ref type
  timezone: string;
  datesSet: DatesSetArg;
  fullDayEventDays: { [dateKey: string]: boolean };
  currentWeekRange: string;
  availableDays: string[];
  userAvailability: any;
  dateFormatLabel: string;
  setHasUserModified: (value: boolean) => void;
}

export const useSchFindTime = ({
  interviewDataRef,
  registerForUpdates,
  updateSharedData,
  schedulerState,
  calendarRef,
  timezone,
  datesSet,
  fullDayEventDays,
  currentWeekRange,
  availableDays,
  userAvailability,
  dateFormatLabel,
  setHasUserModified,
}: UseSchFindTimeProps) => {
  const [lastFindTimeValue, setLastFindTimeValue] = useState<
    string | undefined
  >(undefined);
  const [lastResetAllSlots] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let isProcessing = false;

    const unregisterFindTimeValue = registerForUpdates('findTimeValue', () => {
      if (isProcessing) return;

      const findTimeValue: string | undefined =
        interviewDataRef.current.findTimeValue;

      const freshSchedulerState = interviewDataRef.current.schedulerState || {};
      const currentWeekState = freshSchedulerState[currentWeekRange] || {
        isAllChecked: false,
        days: {},
      };

      if (
        findTimeValue === lastFindTimeValue &&
        currentWeekState.isAllChecked === (findTimeValue === 'this_week')
      ) {
        return;
      }

      isProcessing = true;

      if (
        !findTimeValue &&
        interviewDataRef.current.resetAllSlots &&
        lastResetAllSlots !== true
      ) {
        if (lastFindTimeValue === '') {
          isProcessing = false;
          return;
        }
        setLastFindTimeValue('');
        setHasUserModified(false);

        const resetPayload = {
          schedulerState: {
            ...freshSchedulerState,
            [currentWeekRange]: {
              ...currentWeekState,
              isAllChecked: false,
              days: Object.keys(currentWeekState.days).reduce(
                (acc, dateKey) => ({
                  ...acc,
                  [dateKey]: {
                    ...currentWeekState.days[dateKey],
                    isSelected: false,
                    customSlots: [],
                    removedSlots: [],
                    slotsGenerated: [],
                  },
                }),
                {}
              ),
            },
          },
          findTimeValue: '',
          noSlotsAvailable: false,
        };
        updateSharedData(resetPayload);
        isProcessing = false;
        return;
      } else if (!findTimeValue) {
        setLastFindTimeValue('');
        isProcessing = false;
        return;
      }

      const now: Moment = moment().tz(timezone);
      let currentCalendarWeekStart: Moment = moment
        .tz(datesSet?.start, timezone)
        .startOf('week')
        .day(0);
      if (currentCalendarWeekStart.day() === 6) {
        currentCalendarWeekStart = currentCalendarWeekStart.add(1, 'day');
      }

      let actualCurrentWeek: Moment = moment()
        .tz(timezone)
        .startOf('week')
        .day(0);
      if (actualCurrentWeek.day() === 6) {
        actualCurrentWeek = actualCurrentWeek.add(1, 'day');
      }

      const updatedSchedulerState = { ...freshSchedulerState };
      const expectedSelectedDays: string[] = [];
      let targetWeekRanges: string[] = [];
      let targetWeekToShow: string | null = null;

      const clearBeforeFindTime =
        interviewDataRef.current.clearBeforeFindTime ?? true;

      if (clearBeforeFindTime) {
        Object.keys(updatedSchedulerState).forEach((weekRange) => {
          const weekState = updatedSchedulerState[weekRange];
          const updatedDays = { ...weekState.days };
          Object.keys(updatedDays).forEach((dateKey) => {
            updatedDays[dateKey] = {
              ...updatedDays[dateKey],
              isSelected: false,
              removedSlots: [],
            };
          });
          updatedSchedulerState[weekRange] = {
            ...weekState,
            isAllChecked: false,
            days: updatedDays,
          };
        });
      }

      const checkForAvailableSlots = (weekRanges: string[]) => {
        let hasSlots = false;
        let hasSelectedDays = false;

        for (const weekRange of weekRanges) {
          const weekState = updatedSchedulerState[weekRange];
          if (!weekState) continue;

          for (const dateKey in weekState.days) {
            const dayState = weekState.days[dateKey];
            if (dayState.isSelected) {
              hasSelectedDays = true;
              const activeGeneratedSlots = dayState.slotsGenerated.filter(
                (slot) => !dayState.removedSlots.includes(slot.id)
              );
              if (
                activeGeneratedSlots.length > 0 ||
                dayState.customSlots.length > 0
              ) {
                hasSlots = true;
                break;
              }
            }
          }
          if (hasSlots) break;
        }

        // Only return false (no slots available) if we have selected days but no slots
        // This prevents showing the "no slots available" message when no days are selected
        return !hasSelectedDays || hasSlots;
      };

      if (findTimeValue === 'this_week') {
        const todayWeekStart = now.clone().startOf('week').day(0);
        if (todayWeekStart.day() === 6) {
          todayWeekStart.add(1, 'day');
        }

        const todayWeekRange = schedulerDetailsUtils.normalizeWeekRangeFromDate(
          todayWeekStart.format('MMM D, YYYY'),
          timezone
        );
        let todayWeekState = updatedSchedulerState[todayWeekRange];
        if (!todayWeekState) {
          const updatedDays = {};
          for (let i = 0; i < 7; i++) {
            const day = todayWeekStart.clone().add(i, 'days');
            const dateKey = day.format(dateFormatLabel);
            updatedDays[dateKey] = {
              isSelected: false,
              slotsGenerated: [],
              customSlots: [],
              removedSlots: [],
            };
          }
          todayWeekState = { isAllChecked: false, days: updatedDays };
          updatedSchedulerState[todayWeekRange] = todayWeekState;
        }

        if (todayWeekState.isAllChecked && lastFindTimeValue === 'this_week') {
          isProcessing = false;
          return;
        }

        const updatedDays = { ...todayWeekState.days };
        const expectedSelectedDaysLocal = [];
        for (let i = 0; i < 7; i++) {
          const day = todayWeekStart.clone().add(i, 'days');
          const dateKey = day.format(dateFormatLabel);
          if (!fullDayEventDays[dateKey]) {
            expectedSelectedDaysLocal.push(day.toISOString());
          }
        }

        Object.keys(updatedDays).forEach((dateKey) => {
          const isThisDaySelected = expectedSelectedDaysLocal.some(
            (d) => moment(d).tz(timezone).format(dateFormatLabel) === dateKey
          );
          updatedDays[dateKey] = {
            ...updatedDays[dateKey],
            isSelected: clearBeforeFindTime
              ? isThisDaySelected
              : updatedDays[dateKey].isSelected || isThisDaySelected,
            customSlots: updatedDays[dateKey].customSlots,
            removedSlots: [],
            slotsGenerated: isThisDaySelected
              ? updatedDays[dateKey].slotsGenerated
              : [],
          };
        });

        const allSelected = Object.keys(updatedDays).every(
          (dateKey) =>
            !availableDays.includes(dateKey) || updatedDays[dateKey].isSelected
        );

        updatedSchedulerState[todayWeekRange] = {
          ...todayWeekState,
          isAllChecked: allSelected,
          days: updatedDays,
        };

        if (calendarRef.current && todayWeekRange !== currentWeekRange) {
          const weeksDiff = todayWeekStart.diff(
            currentCalendarWeekStart,
            'weeks'
          );
          if (weeksDiff > 0) {
            for (let i = 0; i < weeksDiff; i++)
              calendarRef.current.getApi().next();
          } else if (weeksDiff < 0) {
            for (let i = 0; i < Math.abs(weeksDiff); i++)
              calendarRef.current.getApi().prev();
          }
        }

        setHasUserModified(false);
        setLastFindTimeValue(findTimeValue);

        const hasSlots = checkForAvailableSlots([todayWeekRange]);

        updateSharedData({
          schedulerState: updatedSchedulerState,
          findTimeValue: 'this_week',
          noSlotsAvailable: !hasSlots,
        });
      } else {
        if (findTimeValue === 'next_week') {
          const nextWeekStart = actualCurrentWeek.clone().add(1, 'week');
          const nextWeekRange =
            schedulerDetailsUtils.normalizeWeekRangeFromDate(
              nextWeekStart.format('MMM D, YYYY'),
              timezone
            );
          for (let i = 0; i < 7; i++) {
            const day = nextWeekStart.clone().add(i, 'days');
            const dateKey = day.format(dateFormatLabel);
            if (!fullDayEventDays[dateKey]) {
              expectedSelectedDays.push(day.toISOString());
            }
          }
          targetWeekRanges = [nextWeekRange];
          targetWeekToShow = nextWeekRange;

          if (calendarRef.current && nextWeekRange !== currentWeekRange) {
            const weeksDiff = nextWeekStart.diff(
              currentCalendarWeekStart,
              'weeks'
            );
            if (weeksDiff > 0) {
              for (let i = 0; i < weeksDiff; i++)
                calendarRef.current.getApi().next();
            } else if (weeksDiff < 0) {
              for (let i = 0; i < Math.abs(weeksDiff); i++)
                calendarRef.current.getApi().prev();
            }
          }
        } else {
          const numDays = parseInt(findTimeValue, 10);
          const currentDay = now.clone().startOf('day');
          let selectedCount = 0;
          const weekRangesSet = new Set<string>();
          let firstSelectedDay: Moment | null = null;
          const maxDaysToCheck = 30;

          let daysChecked = 0;
          while (selectedCount < numDays && daysChecked < maxDaysToCheck) {
            currentDay.add(1, 'day');
            daysChecked++;
            const dateKey = currentDay.format(dateFormatLabel);
            const isDayAvailable = !fullDayEventDays[dateKey];
            const currentStartOfWeek = currentDay
              .clone()
              .startOf('week')
              .day(0);
            if (currentStartOfWeek.day() === 6)
              currentStartOfWeek.add(1, 'day');
            const formattedWeekRange =
              schedulerDetailsUtils.normalizeWeekRangeFromDate(
                currentStartOfWeek.format('MMM D, YYYY'),
                timezone
              );
            const hasDayAvailability = (
              userAvailability[formattedWeekRange] || []
            ).some(
              (slot) =>
                moment.tz(slot.start, timezone).format(dateFormatLabel) ===
                dateKey
            );

            if (isDayAvailable && hasDayAvailability) {
              if (!firstSelectedDay) firstSelectedDay = currentDay.clone();
              expectedSelectedDays.push(currentDay.toISOString());
              selectedCount++;
              weekRangesSet.add(formattedWeekRange);
            }
          }

          targetWeekRanges = Array.from(weekRangesSet);
          if (firstSelectedDay) {
            const firstDayWeekStart = firstSelectedDay
              .clone()
              .startOf('week')
              .day(0);
            if (firstDayWeekStart.day() === 6) firstDayWeekStart.add(1, 'day');
            targetWeekToShow = schedulerDetailsUtils.normalizeWeekRangeFromDate(
              firstDayWeekStart.format('MMM D, YYYY'),
              timezone
            );

            if (calendarRef.current && targetWeekToShow !== currentWeekRange) {
              const weeksDiff = firstDayWeekStart.diff(
                currentCalendarWeekStart,
                'weeks'
              );
              if (weeksDiff > 0) {
                for (let i = 0; i < weeksDiff; i++)
                  calendarRef.current.getApi().next();
              } else if (weeksDiff < 0) {
                for (let i = 0; i < Math.abs(weeksDiff); i++)
                  calendarRef.current.getApi().prev();
              }
            }
          }
        }

        targetWeekRanges.forEach((weekRange) => {
          if (!updatedSchedulerState[weekRange]) {
            const startPart = weekRange.split(' – ')[0];
            const endPart = weekRange.split(' – ')[1] || '';

            // Extract year from endPart if available
            let year: number | null = null;
            if (endPart.includes(',')) {
              const yearMatch = endPart.match(/, (\d{4})/);
              if (yearMatch) {
                year = parseInt(yearMatch[1]);
              }
            }

            // Parse startPart with year if available
            let weekStart: moment.Moment;
            if (year && !startPart.includes(',')) {
              // Start doesn't have year, use year from end
              const dateStr = `${startPart}, ${year}`;
              weekStart = moment
                .tz(dateStr, 'MMM D, YYYY', timezone)
                .startOf('week');
            } else if (startPart.includes(',')) {
              // Start has year
              weekStart = moment
                .tz(startPart, 'MMM D, YYYY', timezone)
                .startOf('week');
            } else {
              // Fallback: no year available
              weekStart = moment
                .tz(startPart, 'MMM D', timezone)
                .startOf('week');
            }
            if (weekStart.day() === 6) weekStart.add(1, 'day');
            const normalizedWeekRange =
              schedulerDetailsUtils.normalizeWeekRangeFromDate(
                weekStart.format('MMM D, YYYY'),
                timezone
              );
            const updatedDays: typeof currentWeekState.days = {};
            for (let i = 0; i < 7; i++) {
              const day = weekStart.clone().add(i, 'days');
              const dateKey = day.format(dateFormatLabel);
              updatedDays[dateKey] = {
                isSelected: false,
                slotsGenerated: [],
                customSlots: [],
                removedSlots: [],
              };
            }
            updatedSchedulerState[normalizedWeekRange] = {
              isAllChecked: false,
              days: updatedDays,
            };
          }

          const weekState = updatedSchedulerState[weekRange];
          const updatedDays = { ...weekState.days };
          let hasSelectedDays = false;

          Object.keys(updatedDays).forEach((dateKey) => {
            const isThisDaySelected = expectedSelectedDays.some(
              (d) => moment(d).tz(timezone).format(dateFormatLabel) === dateKey
            );
            if (isThisDaySelected) hasSelectedDays = true;
            updatedDays[dateKey] = {
              ...updatedDays[dateKey],
              isSelected: clearBeforeFindTime
                ? isThisDaySelected
                : updatedDays[dateKey].isSelected || isThisDaySelected,
              customSlots: updatedDays[dateKey].customSlots,
              removedSlots: [],
            };
          });

          const allSelected = Object.keys(updatedDays).every(
            (dateKey) =>
              !availableDays.includes(dateKey) ||
              updatedDays[dateKey].isSelected
          );

          updatedSchedulerState[weekRange] = {
            ...weekState,
            days: updatedDays,
            isAllChecked: allSelected && hasSelectedDays,
          };
        });

        setHasUserModified(false);
        setLastFindTimeValue(findTimeValue);

        const hasSlots = checkForAvailableSlots(targetWeekRanges);

        updateSharedData({
          schedulerState: updatedSchedulerState,
          findTimeValue,
          noSlotsAvailable: !hasSlots,
        });
      }

      isProcessing = false;
    });

    return () => {
      unregisterFindTimeValue();
    };
  }, [
    registerForUpdates,
    timezone,
    datesSet,
    fullDayEventDays,
    currentWeekRange,
    schedulerState,
    lastFindTimeValue,
    availableDays,
    calendarRef,
    interviewDataRef,
    updateSharedData,
    lastResetAllSlots,
    userAvailability,
    dateFormatLabel,
    setHasUserModified,
  ]);

  return { lastFindTimeValue, setLastFindTimeValue };
};
